import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  ReactNode,
  useMemo,
} from "react";
import axios from "axios";
import { getAccessToken } from "../Components/Utils/TokenRefresh";
import UserContext from "./UserContext";
import { creatureProgressFromTotalXp } from "../Components/Utils/CreatureXpCurve";
import LunaPowerHost from "../Components/Luna/LunaPowerHost";
import { stageForLevel } from "../Components/Luna/lunaModel";
import { LUNA_EVENT } from "../Components/Luna/lunaPowerStore";
import type { LunaPayload } from "../Components/Luna/lunaModel";

/* ───────── Types from your models ───────── */
interface StageAsset {
  stage: number;
  image: string;
}

interface Evolution {
  creatureId: string;
  levelRequired: number;
}

export interface CreatureDefinition {
  creatureId: string;
  name: string;
  element?: string;
  planet?: string;
  family?: string;
  evolvesTo?: Evolution[];
  stageAssets: StageAsset[];
  baseStats?: {
    hp: number;
    atk: number;
    spd: number;
  };
  learnableMoves?: {
    moveId: string;
    learnAtLevel: number;
  }[];
  starter?: boolean;
  /** Evolution levels from the server (seed thresholds); stage 2 at the first, 3 at the second. */
  evolvesAtLevels?: number[];
  /** Display name per stage ("Trisaur", "Tribeast", "Trigonox"). */
  stageNames?: Record<string, string>;
  maxStage?: number;
  /** "wormhole" (default) or an exclusive source: "invite", "duel", "raid". */
  source?: string;
  /** false: never in the Wormhole (exclusive Lunas). */
  obtainable?: boolean;
  /** Invite Lunas: 1, 2 or 3 (1, 3 and 5 friends). */
  inviteTier?: number | null;
}

/** Exclusive Lunas (invite, duel, raid) never appear in the Wormhole. */
export const inWormhole = (d: Pick<CreatureDefinition, "source" | "obtainable">) =>
  d.obtainable !== false && (!d.source || d.source === "wormhole");

export interface UserCreature {
  creatureId: string;
  nickname?: string;
  xp: number;
  level: number;
  stage: number;
  /** Every stage this Luna has reached (each is a Collection entry). */
  stagesCollected?: number[];
  // You may still have this in DB from earlier iterations; it's ignored here:
  isInChamber?: boolean;
}

/* ───────── Evolution event ───────── */
export interface EvolutionEvent {
  creatureId: string;
  creatureName: string;
  oldStage: number;
  newStage: number;
  newLevel: number;
  oldImageUrl: string;
  newImageUrl: string;
}

/* ───────── Context shape ───────── */
interface CharacterInventoryContextType {
  // Data
  creatureDefinitions: CreatureDefinition[];
  ownedCreatureMap: Record<string, UserCreature>;
  ownedCreatures: Set<string>;

  // Active = Chamber (single source of truth)
  activeCreatureId: string | null;
  /** Alias for old callers expecting a chamber id; it’s the same as activeCreatureId. */
  stardustChamberCreatureId: string | null;

  // Helpers / selectors
  getCreatureLevel: (creatureId: string) => number;
  getCreatureXP: (creatureId: string) => number;
  getCreatureXPProgress: (creatureId: string) => {
    level: number;
    currentInLevel: number;
    neededForNext: number;
    pct: number;
  };

  getActiveCreature: () => UserCreature | undefined;
  getActiveDefinition: () => CreatureDefinition | undefined;
  getActiveStageAsset: () => StageAsset | undefined;
  getActiveImageUrl: () => string | undefined;

  // Actions
  setActiveCreature: (creatureId: string) => Promise<void>;

  refreshInventory: () => Promise<void>;
  awardActiveXp: (delta: number) => Promise<void>;

  // Evolution celebration
  evolutionEvent: EvolutionEvent | null;
  clearEvolution: () => void;

  // State
  isLoading: boolean;
  error: string | null;
}

/* ───────── Internals ───────── */
export const CharacterInventoryContext =
  createContext<CharacterInventoryContextType | null>(null);

export const useCharacterInventory = () => {
  const ctx = useContext(CharacterInventoryContext);
  if (!ctx) {
    throw new Error(
      "useCharacterInventory must be used within a CharacterInventoryProvider"
    );
  }
  return ctx;
};

export const CharacterInventoryProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(
    /\/$/,
    ""
  );
  const userContext = useContext(UserContext);

  const [creatureDefinitions, setCreatureDefinitions] = useState<
    CreatureDefinition[]
  >([]);
  const [ownedCreatureMap, setOwnedCreatureMap] = useState<
    Record<string, UserCreature>
  >({});
  const [activeCreatureId, setActiveCreatureId] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [evolutionEvent, setEvolutionEvent] = useState<EvolutionEvent | null>(
    null
  );

  const clearEvolution = useCallback(() => setEvolutionEvent(null), []);

  const fetchInventory = async () => {
    if (!userContext?.user?.onboarding?.choseStarter) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const token = await getAccessToken();
    if (!token) {
      setIsLoading(false);
      setError("[CharacterInventory] No token");
      return;
    }

    const auth = { headers: { Authorization: `Bearer ${token}` } };

    const [defRes, userRes] = await Promise.all([
      axios.get(`${API_BASE}/api/creatures/all`, auth),
      axios.get(`${API_BASE}/api/creatures`, auth),
    ]);

    const defs: CreatureDefinition[] = defRes.data || [];
    setCreatureDefinitions(defs);

    const userData = userRes.data || {};
    const creatures: UserCreature[] = userData.creatures || [];

    const map: Record<string, UserCreature> = {};
    for (const c of creatures) map[c.creatureId] = c;
    setOwnedCreatureMap(map);

    let activeId: string | null = userData.activeCreatureId || null;

    // Default active to first owned if missing; persist to API
    if (!activeId && creatures.length) {
      activeId = creatures[0].creatureId;
      try {
        await axios.put(
          `${API_BASE}/api/creatures/${activeId}/active`,
          {},
          auth
        );
      } catch {
        /* non-fatal */
      }
    }

    setActiveCreatureId(activeId || null);
    setIsLoading(false);
  };

  /* ---------- Fetch defs + user’s creatures ---------- */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await fetchInventory();
      } catch (e: any) {
        if (!cancelled) {
          setError(
            e?.response?.data?.message ||
              e?.message ||
              "[CharacterInventory] Failed to fetch"
          );
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userContext?.user, API_BASE]);

  /* ---------- Luna power-ups: keep owned Lunas in step with every award ---------- */
  useEffect(() => {
    const onLuna = (e: Event) => {
      const p = (e as CustomEvent<LunaPayload>).detail;
      if (!p?.creatureId || !Number.isFinite(p.xp as number)) return;
      const cid = p.creatureId;
      setOwnedCreatureMap((prev) => {
        const cur = prev[cid];
        if (!cur) return prev;
        const stage = Math.max(cur.stage || 1, Number(p.stage) || 1);
        const collected = new Set([...(cur.stagesCollected || []), ...Array.from({ length: stage }, (_, i) => i + 1)]);
        return {
          ...prev,
          [cid]: { ...cur, xp: Number(p.xp), level: Number(p.level) || cur.level, stage, stagesCollected: Array.from(collected).sort((a, b) => a - b) },
        };
      });
    };
    window.addEventListener(LUNA_EVENT, onLuna);
    return () => window.removeEventListener(LUNA_EVENT, onLuna);
  }, []);

  /* ---------- Derived sets ---------- */
  const ownedCreatures = useMemo(
    () => new Set(Object.keys(ownedCreatureMap)),
    [ownedCreatureMap]
  );

  /* ---------- Helpers ---------- */

  const computeLevelFromXp = (xp: number) =>
    creatureProgressFromTotalXp(xp).level;

  // One evolution rule with the server (seed thresholds, else evolvesTo).
  const computeStageFromDefinition = (
    def: CreatureDefinition | undefined,
    level: number
  ) => (def ? stageForLevel(def, level) : 1);

  const getCreatureLevel = (creatureId: string): number =>
    ownedCreatureMap[creatureId]?.level ?? 0;

  const getCreatureXP = (creatureId: string): number =>
    ownedCreatureMap[creatureId]?.xp ?? 0;

  /**
   * XP curve:
   * level 1 -> need 100
   * level 2 -> need 200
   * level 3 -> need 300
   * ...
   * We compute display progress from TOTAL XP using creatureProgressFromTotalXp.
   */
  const getCreatureXPProgress = (creatureId: string) => {
    const xp = ownedCreatureMap[creatureId]?.xp ?? 0;
    return creatureProgressFromTotalXp(xp);
  };

  const getActiveCreature = (): UserCreature | undefined =>
    activeCreatureId ? ownedCreatureMap[activeCreatureId] : undefined;

  const getActiveDefinition = (): CreatureDefinition | undefined => {
    if (!activeCreatureId) return undefined;
    return creatureDefinitions.find((d) => d.creatureId === activeCreatureId);
  };

  const getActiveStageAsset = (): StageAsset | undefined => {
    const uc = getActiveCreature();
    const def = getActiveDefinition();
    if (!uc || !def) return undefined;
    // Find image by current stage; fallback to highest available stage
    return (
      def.stageAssets.find((s) => s.stage === uc.stage) ||
      [...def.stageAssets].sort((a, b) => b.stage - a.stage)[0]
    );
  };

  const getActiveImageUrl = (): string | undefined =>
    getActiveStageAsset()?.image;

  /* ---------- Actions ---------- */
  const refreshInventory = async () => {
    try {
      await fetchInventory();
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "[CharacterInventory] Failed to refresh"
      );
    }
  };

  const setActiveCreature = async (creatureId: string) => {
    if (!ownedCreatures.has(creatureId)) {
      throw new Error("Creature not owned");
    }
    const token = await getAccessToken();
    if (!token) throw new Error("No token");

    const auth = { headers: { Authorization: `Bearer ${token}` } };
    await axios.put(`${API_BASE}/api/creatures/${creatureId}/active`, {}, auth);
    setActiveCreatureId(creatureId);
  };

  // ✅ optimistic stardust add for instant UI
  const awardActiveXp = async (delta: number) => {
    const d = Number(delta);
    if (!Number.isFinite(d) || d <= 0) return;
    if (!activeCreatureId) return;

    const cid = activeCreatureId;

    setOwnedCreatureMap((prev) => {
      const current = prev[cid];
      if (!current) return prev;

      const oldStage = current.stage ?? 1;
      const newXp = Number(current.xp || 0) + d;
      const newLevel = computeLevelFromXp(newXp);

      const def = creatureDefinitions.find((x) => x.creatureId === cid);
      const newStage = computeStageFromDefinition(def, newLevel);

      // Evolution is decided by the server and celebrated by
      // Luna/LunaEvolution (lunaPowerStore); nothing to show here.
      void oldStage;

      return {
        ...prev,
        [cid]: {
          ...current,
          xp: newXp,
          level: newLevel,
          stage: newStage,
        },
      };
    });
  };

  /* ---------- Context value ---------- */
  const ctxValue: CharacterInventoryContextType = {
    creatureDefinitions,
    ownedCreatureMap,
    ownedCreatures,

    activeCreatureId,
    // Alias so other parts of the app can still read chamber id:
    stardustChamberCreatureId: activeCreatureId,

    getCreatureLevel,
    getCreatureXP,
    getCreatureXPProgress,

    getActiveCreature,
    getActiveDefinition,
    getActiveStageAsset,
    getActiveImageUrl,

    setActiveCreature,

    refreshInventory,
    awardActiveXp,

    evolutionEvent,
    clearEvolution,

    isLoading,
    error,
  };

  return (
    <CharacterInventoryContext.Provider value={ctxValue}>
      {children}
      <LunaPowerHost />
    </CharacterInventoryContext.Provider>
  );
};
